import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Button, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Map as ImmutableMap } from 'immutable';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useDeleteConfirm from '@sophocles/hooks/useDeleteConfirm';

import SpecialInstructionDialog from '../dialogs/SpecialInstructionDialog';

// V2 Special Instructions list.  Renders one Card per instruction
// with category chip, content, attribution, and edit / delete
// controls.  Replaces the legacy `SpecialInstructionPanel` +
// `SiteGroupInstructionsSection` pair.
//
// Component is presentation + dialog orchestration only — caller
// wires up the underlying CRUD thunks via the `*Thunk` props.
//
// Props:
//   instructions  : Immutable.List<Map> | array — current rows
//   isLoading     : optional, render loading state
//   onCreate      : (values) => Promise   — fired for new
//   onUpdate      : (id, values) => Promise — fired for edit
//   onDelete      : (id) => Promise       — fired for delete
//   onFetch       : (id) => Promise<full> — load full instruction (for edit)
//   onReload      : () => void            — refetch list (called after CRUD)

const APPLIES_TO_LABEL = {
  0: 'Other',
  1: 'RMA',
  2: 'Orders',
  3: 'RMA and Orders',
};

const APPLIES_TO_COLOR = {
  1: 'info',
  2: 'success',
  3: 'primary',
};

const getField = (obj, key, fallback = undefined) => {
  if (obj == null) return fallback;
  if (ImmutableMap.isMap(obj)) return obj.get(key, fallback);
  return obj[key] ?? fallback;
};

const userName = user => {
  if (!user) return null;
  const get = ImmutableMap.isMap(user) ? key => user.get(key) : key => user[key];
  const first = get('first_name') ?? '';
  const last = get('last_name') ?? '';
  return [first, last].filter(Boolean).join(' ').trim() || get('username') || null;
};

function InstructionCard({ instruction, onEdit, onDelete }) {
  const { t } = useTranslation();
  const id = getField(instruction, 'id');
  const appliesTo = getField(instruction, 'applies_to', 0);
  const text = getField(instruction, 'instructions', '');
  const updatedBy = getField(instruction, 'updated_by');
  const createdBy = getField(instruction, 'created_by');
  const updated = getField(instruction, 'updated');
  const created = getField(instruction, 'created');
  const stamp = updated || created;
  const stampUser = updatedBy || createdBy;
  const stampLabel = updated ? t('general.updated', 'Updated') : t('general.created', 'Created');

  return (
    <Box
      sx={{
        borderRadius: 'var(--card-radius)',
        border: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        p: 2,
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              color={APPLIES_TO_COLOR[appliesTo] || 'default'}
              label={APPLIES_TO_LABEL[appliesTo] || `#${appliesTo}`}
            />
            {stampUser ? (
              <Tooltip title={stamp ? moment(stamp).format('L, LTS') : ''}>
                <Typography variant="caption" color="text.secondary">
                  {stampLabel} {t('general.byUser', 'by {{user}}', { user: userName(stampUser) || '—' })}
                  {stamp ? ` — ${moment(stamp).fromNow()}` : ''}
                </Typography>
              </Tooltip>
            ) : null}
          </Stack>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {text}
          </Typography>
        </Stack>
        <Stack direction="row">
          <Tooltip title={t('actions.edit', 'Edit')}>
            <IconButton size="small" onClick={() => onEdit(id)}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('actions.delete', 'Delete')}>
            <IconButton size="small" onClick={() => onDelete(id)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
}

export default function SpecialInstructionsList({ instructions, onCreate, onUpdate, onDelete, onFetch, onReload }) {
  const { t } = useTranslation();
  const [dialogId, setDialogId] = useState(undefined); // undefined=closed, null=new, number=edit
  const { confirmDelete, dialogElement: deleteConfirmDialog } = useDeleteConfirm();

  // Refetch on first mount; parent should also pass a fresh
  // `instructions` list so we re-render.  The reload call here is
  // a safety net for callers who haven't pre-loaded.
  useEffect(() => {
    onReload?.();
  }, []);

  // Normalize Immutable.List → array of plain objects up front so
  // the render path stays simple (the entity reducer returns
  // Immutable still; JSON-from-API would be plain).
  const items = instructions ? (instructions.toJS ? instructions.toJS() : instructions) : [];

  const handleSaved = () => {
    setDialogId(undefined);
    onReload?.();
  };

  const handleDelete = id => {
    confirmDelete(id, {
      entityLabel: t('specialInstructions.specialInstruction'),
      onConfirm: async targetId => {
        await onDelete(targetId);
        onReload?.();
      },
    });
  };

  return (
    <Stack spacing={1.5}>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('specialInstructions.empty', 'No special instructions yet.')}
        </Typography>
      ) : (
        items.map(ins => {
          const id = getField(ins, 'id');
          return (
            <InstructionCard
              key={id}
              instruction={ins}
              onEdit={() => setDialogId(id)}
              onDelete={() => handleDelete(id)}
            />
          );
        })
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={() => setDialogId(null)}>
          {t('specialInstructions.addInstruction', 'Add Instruction')}
        </Button>
      </Box>

      {dialogId !== undefined ? (
        <SpecialInstructionDialog
          open
          instructionId={dialogId}
          onClose={() => setDialogId(undefined)}
          onCreate={onCreate}
          onUpdate={onUpdate}
          onFetch={onFetch}
          onSaved={handleSaved}
        />
      ) : null}

      {deleteConfirmDialog}
    </Stack>
  );
}
